import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import {
  AssistantConversationState,
  AssistantConversationSummary,
  AssistantCreditCheckout,
  AssistantCreditPack,
  AssistantCustomTemplateSummary,
  AssistantWebsiteAsset,
  AssistantMeteringState,
  AssistantPendingImage,
  AssistantUsageHistory,
  AssistantStreamCallbacks
} from '../models/assistant.models';

interface AssistantApiError {
  code: number;
  message: string;
}

const REQUEST_TIMEOUT_MS = 10_000;
const WRITE_TIMEOUT_MS = 30_000;
const IMAGE_TIMEOUT_MS = 15_000;
const STREAM_IDLE_TIMEOUT_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class AssistantService {
  private readonly apiUrl = `${environment.apiUrl}/assistant`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {}

  getMetering(): Observable<AssistantMeteringState> {
    return this.http
      .get<{ success: boolean; metering: AssistantMeteringState }>(`${this.apiUrl}/status`)
      .pipe(timeout(REQUEST_TIMEOUT_MS), map((response) => response.metering), catchError(this.handleError));
  }

  getUsageHistory(): Observable<AssistantUsageHistory> {
    return this.http
      .get<{ success: boolean; usage: AssistantUsageHistory }>(`${this.apiUrl}/usage`)
      .pipe(timeout(REQUEST_TIMEOUT_MS), map((response) => response.usage), catchError(this.handleError));
  }

  listCreditPacks(): Observable<AssistantCreditPack[]> {
    return this.http
      .get<{ success: boolean; packs: AssistantCreditPack[] }>(`${this.apiUrl}/credits/packs`)
      .pipe(timeout(REQUEST_TIMEOUT_MS), map((response) => response.packs), catchError(this.handleError));
  }

  prepareCreditCheckout(creditPackId: string): Observable<AssistantCreditCheckout> {
    return this.http
      .post<{ success: boolean; checkout: AssistantCreditCheckout }>(`${this.apiUrl}/credits/checkout/prepare`, { creditPackId })
      .pipe(timeout(WRITE_TIMEOUT_MS), map((response) => response.checkout), catchError(this.handleError));
  }

  completeCreditCheckout(payload: {
    checkoutSessionId: string;
    customerId: string;
    transactionId: string;
  }): Observable<AssistantMeteringState> {
    return this.http
      .post<{ success: boolean; metering: AssistantMeteringState }>(`${this.apiUrl}/credits/checkout/complete`, payload)
      .pipe(timeout(WRITE_TIMEOUT_MS), map((response) => response.metering), catchError(this.handleError));
  }

  listCustomTemplates(): Observable<AssistantCustomTemplateSummary[]> {
    return this.http
      .get<{ success: boolean; templates: AssistantCustomTemplateSummary[] }>(`${this.apiUrl}/templates`)
      .pipe(timeout(REQUEST_TIMEOUT_MS), map((response) => response.templates), catchError(this.handleError));
  }

  saveCustomTemplate(
    sourceWebsiteId: string,
    name: string,
    description?: string | null
  ): Observable<AssistantCustomTemplateSummary> {
    return this.http
      .post<{ success: boolean; template: AssistantCustomTemplateSummary }>(`${this.apiUrl}/templates`, {
        sourceWebsiteId,
        name,
        description
      })
      .pipe(timeout(WRITE_TIMEOUT_MS), map((response) => response.template), catchError(this.handleError));
  }

  updateCustomTemplate(templateId: string, payload: Record<string, unknown>): Observable<AssistantCustomTemplateSummary> {
    return this.http
      .patch<{ success: boolean; template: AssistantCustomTemplateSummary }>(
        `${this.apiUrl}/templates/${encodeURIComponent(templateId)}`,
        payload
      )
      .pipe(timeout(WRITE_TIMEOUT_MS), map((response) => response.template), catchError(this.handleError));
  }

  deleteCustomTemplate(templateId: string): Observable<void> {
    return this.http
      .delete<{ success: boolean }>(`${this.apiUrl}/templates/${encodeURIComponent(templateId)}`)
      .pipe(timeout(WRITE_TIMEOUT_MS), map(() => undefined), catchError(this.handleError));
  }

  promoteImageToWebsiteAsset(imageId: string, websiteId: string): Observable<AssistantWebsiteAsset> {
    return this.http
      .post<{ success: boolean; asset: AssistantWebsiteAsset }>(`${this.apiUrl}/assets/promote`, { imageId, websiteId })
      .pipe(timeout(WRITE_TIMEOUT_MS), map((response) => response.asset), catchError(this.handleError));
  }

  createWebsiteFromCustomTemplate(
    templateId: string,
    payload: { name: string; slug?: string; description?: string | null }
  ): Observable<{ publicId: string }> {
    return this.http
      .post<{ success: boolean; website: { publicId: string } }>(
        `${this.apiUrl}/templates/${encodeURIComponent(templateId)}/create-website`,
        payload
      )
      .pipe(timeout(WRITE_TIMEOUT_MS), map((response) => response.website), catchError(this.handleError));
  }

  listConversations(): Observable<AssistantConversationSummary[]> {
    return this.http
      .get<{ success: boolean; conversations: AssistantConversationSummary[] }>(`${this.apiUrl}/conversations`)
      .pipe(timeout(REQUEST_TIMEOUT_MS), map((response) => response.conversations), catchError(this.handleError));
  }

  getConversation(conversationId: string): Observable<AssistantConversationState> {
    return this.http
      .get<{ success: boolean; conversation: AssistantConversationSummary; messages: AssistantConversationState['messages'] }>(
        `${this.apiUrl}/conversations/${encodeURIComponent(conversationId)}`
      )
      .pipe(
        timeout(REQUEST_TIMEOUT_MS),
        map((response) => ({ conversation: response.conversation, messages: response.messages })),
        catchError(this.handleError)
      );
  }

  renameConversation(conversationId: string, title: string): Observable<AssistantConversationSummary> {
    return this.http
      .patch<{ success: boolean; conversation: AssistantConversationSummary }>(
        `${this.apiUrl}/conversations/${encodeURIComponent(conversationId)}`,
        { title }
      )
      .pipe(timeout(WRITE_TIMEOUT_MS), map((response) => response.conversation), catchError(this.handleError));
  }

  deleteConversation(conversationId: string): Observable<void> {
    return this.http
      .delete<{ success: boolean }>(`${this.apiUrl}/conversations/${encodeURIComponent(conversationId)}`)
      .pipe(timeout(WRITE_TIMEOUT_MS), map(() => undefined), catchError(this.handleError));
  }

  markActionApplied(actionId: string): Observable<void> {
    return this.http
      .post<{ success: boolean }>(`${this.apiUrl}/actions/${encodeURIComponent(actionId)}/complete`, {})
      .pipe(timeout(WRITE_TIMEOUT_MS), map(() => undefined), catchError(this.handleError));
  }

  async getImageUrl(imageId: string, signal?: AbortSignal): Promise<string> {
    const headers = this.authorizedHeaders();
    const request = this.createTimeoutAbort(signal, IMAGE_TIMEOUT_MS);
    try {
      const response = await fetch(`${this.apiUrl}/images/${encodeURIComponent(imageId)}`, {
        headers,
        credentials: 'include',
        signal: request.signal
      });
      if (!response.ok) {
        throw new Error('The attached assistant image is no longer available.');
      }
      return URL.createObjectURL(await response.blob());
    } catch (error) {
      if (request.timedOut()) {
        throw new Error('The assistant image took too long to load. Please try again.');
      }
      throw error;
    } finally {
      request.dispose();
    }
  }

  async streamChat(
    payload: {
      conversationId?: string;
      contextWebsiteId?: string;
      message: string;
      images: AssistantPendingImage[];
    },
    callbacks: AssistantStreamCallbacks,
    signal: AbortSignal
  ): Promise<void> {
    const request = this.createTimeoutAbort(signal, STREAM_IDLE_TIMEOUT_MS);
    try {
      const response = await fetch(`${this.apiUrl}/chat`, {
        method: 'POST',
        headers: {
          ...this.authorizedHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream'
        },
        credentials: 'include',
        signal: request.signal,
        body: JSON.stringify(payload)
      });
      request.reset();
      if (!response.ok || !response.body) {
        callbacks.onError(await this.readSafeError(response), response.status);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        request.reset();
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const event of events) {
          this.handleStreamEvent(event, callbacks);
        }
      }
      buffer += decoder.decode();
      if (buffer.trim()) {
        this.handleStreamEvent(buffer, callbacks);
      }
    } catch (error) {
      if (request.timedOut()) {
        callbacks.onError('BizzWeaver AI stopped because the connection was inactive for too long. Try again.', 504);
        return;
      }
      throw error;
    } finally {
      request.dispose();
    }
  }

  private createTimeoutAbort(externalSignal: AbortSignal | undefined, timeoutMs: number): {
    signal: AbortSignal;
    reset: () => void;
    timedOut: () => boolean;
    dispose: () => void;
  } {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let didTimeout = false;
    const abortFromCaller = () => controller.abort();
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        didTimeout = true;
        controller.abort();
      }, timeoutMs);
    };
    if (externalSignal?.aborted) {
      controller.abort();
    } else {
      externalSignal?.addEventListener('abort', abortFromCaller, { once: true });
      reset();
    }
    return {
      signal: controller.signal,
      reset,
      timedOut: () => didTimeout,
      dispose: () => {
        if (timer) clearTimeout(timer);
        externalSignal?.removeEventListener('abort', abortFromCaller);
      }
    };
  }

  private handleStreamEvent(rawEvent: string, callbacks: AssistantStreamCallbacks): void {
    const eventName = rawEvent
      .split('\n')
      .find((line) => line.startsWith('event:'))
      ?.slice('event:'.length)
      .trim();
    const dataText = rawEvent
      .split('\n')
      .find((line) => line.startsWith('data:'))
      ?.slice('data:'.length)
      .trim();
    if (!eventName || !dataText) return;
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(dataText) as Record<string, unknown>;
    } catch {
      return;
    }
    switch (eventName) {
      case 'conversation':
        if (typeof data['conversationId'] === 'string') callbacks.onConversation(data['conversationId'], typeof data['title'] === 'string' ? data['title'] : 'New conversation');
        break;
      case 'delta':
        if (typeof data['delta'] === 'string') callbacks.onDelta(data['delta']);
        break;
      case 'action':
        if (data['action'] && typeof data['action'] === 'object') callbacks.onAction(data['action'] as Parameters<AssistantStreamCallbacks['onAction']>[0]);
        break;
      case 'tool':
        if (
          typeof data['id'] === 'string'
          && typeof data['name'] === 'string'
          && typeof data['label'] === 'string'
          && (data['phase'] === 'running' || data['phase'] === 'complete' || data['phase'] === 'failed')
        ) {
          callbacks.onTool(data as unknown as Parameters<AssistantStreamCallbacks['onTool']>[0]);
        }
        break;
      case 'usage':
        if (typeof data['inputTokens'] === 'number' && typeof data['outputTokens'] === 'number' && typeof data['totalTokens'] === 'number' && typeof data['estimatedCostNanoUsd'] === 'number') {
          callbacks.onUsage(data as unknown as Parameters<AssistantStreamCallbacks['onUsage']>[0]);
        }
        break;
      case 'status':
        if (typeof data['message'] === 'string') callbacks.onStatus(data['message']);
        break;
      case 'done':
        callbacks.onDone(typeof data['messageId'] === 'string' ? data['messageId'] : '');
        break;
      case 'error':
        callbacks.onError(typeof data['message'] === 'string' ? data['message'] : 'BizzWeaver AI could not complete that request.', typeof data['code'] === 'number' ? data['code'] : undefined);
        break;
      default:
        break;
    }
  }

  private authorizedHeaders(): HeadersInit {
    const accessToken = this.authService.getAccessToken();
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  }

  private async readSafeError(response: Response): Promise<string> {
    const payload = await response.json().catch(() => null) as { error?: { message?: string }; message?: string } | null;
    return payload?.error?.message ?? payload?.message ?? 'BizzWeaver AI could not complete that request.';
  }

  private handleError = (error: unknown): Observable<never> => {
    if (error && typeof error === 'object' && 'name' in error && error.name === 'TimeoutError') {
      return throwError(() => ({ code: 504, message: 'BizzWeaver AI took too long to respond. Please try again.' } as AssistantApiError));
    }
    const response = error as HttpErrorResponse;
    const message = response.error?.error?.message ?? response.error?.message ?? response.message ?? 'BizzWeaver AI could not complete that request.';
    return throwError(() => ({ code: response.status, message } as AssistantApiError));
  };
}
